import React, { useState, useMemo, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import YandexAnalytics from './components/YandexAnalytics';

interface LumiReportingContainerProps {
    currentUser?: any;
    theme?: 'light' | 'dark';
    language?: 'en' | 'sv';
}

// Translations
const translations = {
    en: {
        title: 'Reporting',
        subtitle: 'Lumi Tours Admin',
        thisMonth: 'This Month',
        last7Days: 'Last 7 Days',
        customRange: 'Custom Range',
        totalSales: 'TOTAL SALES',
        activeUsers: 'ACTIVE USERS',
        bookings: 'BOOKINGS',
        revenue: 'REVENUE',
        weeklyRevenue: 'Weekly Revenue',
        weeklyIncome: 'Veckoinkomst',
        vsLastWeek: 'vs last week',
        topDestinations: 'Top Destinations',
        recentBookings: 'Recent Bookings',
        settings: 'Settings',
        apiConfig: 'API Configuration',
        googleAnalytics: 'Google Analytics',
        tourvisioApi: 'Tourvisio API',
        metaAds: 'Meta Ads',
        connected: 'Connected',
        notConnected: 'Not Connected',
        connect: 'Connect',
        saveSettings: 'Save Settings'
    },
    sv: {
        title: 'Rapporter',
        subtitle: 'Lumi Tours Admin',
        thisMonth: 'Denna Månad',
        last7Days: 'Senaste 7 Dagar',
        customRange: 'Anpassat',
        totalSales: 'TOTAL FÖRSÄLJNING',
        activeUsers: 'AKTIVA ANVÄNDARE',
        bookings: 'BOKNINGAR',
        revenue: 'INTÄKTER',
        weeklyRevenue: 'Veckovis Intäkt',
        weeklyIncome: 'Veckoinkomst',
        vsLastWeek: 'mot förra veckan',
        topDestinations: 'Topp Destinationer',
        recentBookings: 'Senaste Bokningar',
        settings: 'Inställningar',
        apiConfig: 'API-konfiguration',
        googleAnalytics: 'Google Analytics',
        tourvisioApi: 'Tourvisio API',
        metaAds: 'Meta Ads',
        connected: 'Ansluten',
        notConnected: 'Ej ansluten',
        connect: 'Anslut',
        saveSettings: 'Spara Inställningar'
    }
};



const LumiReportingContainer: React.FC<LumiReportingContainerProps> = ({
    currentUser,
    theme = 'dark',
    language = 'en'
}) => {
    const [dateRange, setDateRange] = useState<'today' | 'month' | 'week' | 'custom'>('month');
    const [activeTab, setActiveTab] = useState<'overview' | 'analytics' | 'destinations' | 'bookings'>('analytics');
    const [showApiKeyModal, setShowApiKeyModal] = useState<string | null>(null);
    const [apiKeyInput, setApiKeyInput] = useState('');
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');

    // API connection states (check localStorage)
    const [apiConnections, setApiConnections] = useState({
        googleAnalytics: localStorage.getItem('reporting_api_ga') ? true : false,
        tourvisio: localStorage.getItem('reporting_api_tourvisio') ? true : false,
        metaAds: localStorage.getItem('reporting_api_meta') ? true : false
    });

    const [dashboardData, setDashboardData] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    const isDark = theme === 'dark';
    const t = translations[language] || translations.sv;

    useEffect(() => {
        const fetchDashboardData = async () => {
            setLoading(true);
            try {
                const res = await fetch(`/api/reporting/dashboard?period=${dateRange === 'custom' ? 'month' : dateRange}`);
                const data = await res.json();
                if (data.success) {
                    setDashboardData(data);
                }
            } catch (error) {
                console.error('Fetch error', error);
            } finally {
                setLoading(false);
            }
        };
        fetchDashboardData();
    }, [dateRange]);

    const weeklyData = dashboardData?.weeklyData || [];
    const weeklyTotal = weeklyData.reduce((sum: number, d: any) => sum + d.revenue, 0);

    // KPI Stats
    const stats = dashboardData?.stats || {
        totalSales: { value: '€0', change: '0%', positive: true },
        activeUsers: { value: '0', change: '0%', positive: true },
        bookings: { value: '0', change: '0%', positive: true },
        revenue: { value: '€0', change: '0%', positive: true }
    };

    const topDestinations = dashboardData?.topDestinations || [];
    const recentBookings = dashboardData?.recentBookings || [];
    const bookingStats = dashboardData?.bookingStats || { confirmed: 0, pending: 0, cancelled: 0 };

    // Theme classes
    const bgMain = isDark
        ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'
        : 'bg-gradient-to-br from-gray-50 via-white to-gray-100';
    const bgCard = isDark
        ? 'bg-slate-800/80 backdrop-blur-sm border border-slate-700/50'
        : 'bg-white/90 backdrop-blur-sm border border-gray-200/50 shadow-sm';
    const textPrimary = isDark ? 'text-white' : 'text-gray-900';
    const textSecondary = isDark ? 'text-slate-400' : 'text-gray-500';
    const accent = 'text-teal-500';

    const saveApiKey = (service: string) => {
        if (apiKeyInput.length > 5) {
            localStorage.setItem(`reporting_api_${service}`, apiKeyInput);
            setApiConnections(prev => ({ ...prev, [service]: true }));
            setShowApiKeyModal(null);
            setApiKeyInput('');
        }
    };

    return (
        <div className={`h-full overflow-y-auto ${bgMain} ${textPrimary} font-sans`}>
            {/* Header */}
            <div className="px-6 py-4 flex items-center justify-between border-b border-slate-700/30">
                <div>
                    <h1 className="text-2xl font-bold">{t.title}</h1>
                    <p className={`text-sm ${textSecondary}`}>{t.subtitle}</p>
                </div>
            </div>

            {/* Date Range Tabs */}
            <div className="px-6 py-4">
                <div className="flex gap-2 flex-wrap items-center">
                    {[
                        { id: 'today', label: language === 'sv' ? 'Idag' : 'Today' },
                        { id: 'month', label: t.thisMonth },
                        { id: 'week', label: t.last7Days },
                        { id: 'custom', label: t.customRange }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setDateRange(tab.id as any)}
                            className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${dateRange === tab.id
                                ? 'bg-teal-500 text-white shadow-lg shadow-teal-500/30'
                                : `${isDark ? 'bg-slate-800 text-slate-300 hover:bg-slate-700' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`
                                }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                    {/* Custom Date Inputs */}
                    {dateRange === 'custom' && (
                        <div className="flex items-center gap-2 ml-2">
                            <span className={`text-sm ${textSecondary}`}>📅</span>
                            <input
                                type="date"
                                value={customStart}
                                onChange={(e) => setCustomStart(e.target.value)}
                                max={customEnd || undefined}
                                className={`px-3 py-1.5 rounded-lg text-sm border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                            />
                            <span className={textSecondary}>→</span>
                            <input
                                type="date"
                                value={customEnd}
                                onChange={(e) => setCustomEnd(e.target.value)}
                                min={customStart || undefined}
                                max={new Date().toISOString().split('T')[0]}
                                className={`px-3 py-1.5 rounded-lg text-sm border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="px-6 mb-4">
                <div className={`flex gap-1 p-1 rounded-xl ${isDark ? 'bg-slate-800/50' : 'bg-gray-100'}`}>
                    {[
                        { id: 'analytics', label: '🤖 AI & Yandex', labelSv: 'AI & Yandex' },
                        { id: 'overview', label: '📊 Overview', labelSv: 'Översikt' },
                        { id: 'destinations', label: '🌍 Destinations', labelSv: 'Destinationer' },
                        { id: 'bookings', label: '📋 Bookings', labelSv: 'Bokningar' }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id as any)}
                            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id
                                ? 'bg-teal-500 text-white'
                                : `${textSecondary} hover:${textPrimary}`
                                }`}
                        >
                            {language === 'sv' ? tab.labelSv : tab.label.split(' ')[1]}
                        </button>
                    ))}
                </div>
            </div>

            {/* Main Content */}
            <div className="px-6 pb-20">
                {activeTab === 'overview' && (
                    <>
                        {/* KPI Cards */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                            {[
                                { key: 'totalSales', label: t.totalSales, ...stats.totalSales },
                                { key: 'activeUsers', label: t.activeUsers, ...stats.activeUsers },
                                { key: 'bookings', label: t.bookings, ...stats.bookings },
                                { key: 'revenue', label: t.revenue, ...stats.revenue }
                            ].map(stat => (
                                <div key={stat.key} className={`p-5 rounded-2xl ${bgCard}`}>
                                    <p className={`text-xs font-bold tracking-wider mb-2 ${textSecondary}`}>
                                        {stat.label}
                                    </p>
                                    <p className="text-3xl font-black">{stat.value}</p>
                                    <span className={`text-xs font-bold flex items-center gap-1 mt-2 ${stat.positive ? 'text-green-500' : 'text-red-500'
                                        }`}>
                                        {stat.positive ? '📈' : '📉'} {stat.change}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {/* Weekly Revenue Chart */}
                        <div className={`p-6 rounded-2xl mb-6 ${bgCard}`}>
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h3 className="font-bold text-lg">{t.weeklyRevenue}</h3>
                                    <p className={`text-xs ${textSecondary}`}>{t.weeklyIncome}</p>
                                </div>
                                <div className="text-right">
                                    <p className={`text-2xl font-black ${accent}`}>
                                        €{weeklyTotal.toLocaleString()}
                                    </p>
                                    <p className="text-xs text-green-500">+12% {t.vsLastWeek}</p>
                                </div>
                            </div>
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
                                    <AreaChart data={weeklyData}>
                                        <defs>
                                            <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.3} />
                                                <stop offset="95%" stopColor="#14b8a6" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e2e8f0'} vertical={false} />
                                        <XAxis
                                            dataKey="day"
                                            stroke={isDark ? '#64748b' : '#94a3b8'}
                                            fontSize={12}
                                            tickLine={false}
                                        />
                                        <YAxis hide />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: isDark ? '#1e293b' : '#fff',
                                                border: 'none',
                                                borderRadius: '12px',
                                                boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
                                            }}
                                            formatter={(value: number) => [`€${value.toLocaleString()}`, 'Revenue']}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="revenue"
                                            stroke="#14b8a6"
                                            strokeWidth={3}
                                            fill="url(#colorRevenue)"
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Top Destinations */}
                        <div className={`p-6 rounded-2xl ${bgCard}`}>
                            <h3 className="font-bold text-lg mb-4">{t.topDestinations}</h3>
                            <div className="space-y-3">
                                {topDestinations.map((dest, i) => (
                                    <div key={dest.name} className={`flex items-center gap-4 p-3 rounded-xl ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                                        <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${i === 0 ? 'bg-yellow-500 text-white' :
                                            i === 1 ? 'bg-slate-400 text-white' :
                                                i === 2 ? 'bg-orange-600 text-white' :
                                                    `${isDark ? 'bg-slate-600' : 'bg-gray-200'} ${textSecondary}`
                                            }`}>
                                            {i + 1}
                                        </span>
                                        <div className="flex-1">
                                            <p className="font-bold">{dest.name}</p>
                                            <p className={`text-xs ${textSecondary}`}>{dest.bookings} bookings</p>
                                        </div>
                                        <p className={`font-bold ${accent}`}>€{dest.revenue.toLocaleString()}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )}

                {activeTab === 'analytics' && (
                    <div className="space-y-8">
                        <AnalyticsDashboard theme={theme} language={language} />

                        {/* Section Divider */}
                        <div className="flex items-center gap-4 py-2">
                            <div className={`flex-1 h-px ${isDark ? 'bg-slate-600' : 'bg-gray-300'}`} />
                            <span className={`text-sm font-bold tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>🌐 YANDEX METRIKA</span>
                            <div className={`flex-1 h-px ${isDark ? 'bg-slate-600' : 'bg-gray-300'}`} />
                        </div>

                        <YandexAnalytics theme={theme} language={language} dateRange={dateRange} />
                    </div>
                )}

                {activeTab === 'settings' && (
                    <div className={`p-6 rounded-2xl ${bgCard}`}>
                        <h3 className="font-bold text-lg mb-6">{t.apiConfig}</h3>
                        <div className="space-y-4">
                            {[
                                { id: 'googleAnalytics', name: t.googleAnalytics, icon: '📊', connected: apiConnections.googleAnalytics },
                                { id: 'tourvisio', name: t.tourvisioApi, icon: '✈️', connected: apiConnections.tourvisio },
                                { id: 'metaAds', name: t.metaAds, icon: '📱', connected: apiConnections.metaAds }
                            ].map(api => (
                                <div key={api.id} className={`flex items-center justify-between p-4 rounded-xl ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                                    <div className="flex items-center gap-3">
                                        <span className="text-2xl">{api.icon}</span>
                                        <div>
                                            <p className="font-bold">{api.name}</p>
                                            <p className={`text-xs ${api.connected ? 'text-green-500' : 'text-red-400'}`}>
                                                {api.connected ? `✓ ${t.connected}` : `✗ ${t.notConnected}`}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setShowApiKeyModal(api.id)}
                                        className={`px-4 py-2 rounded-xl text-sm font-medium ${api.connected
                                            ? `${isDark ? 'bg-slate-600' : 'bg-gray-200'} ${textSecondary}`
                                            : 'bg-teal-500 text-white hover:bg-teal-600'
                                            }`}
                                    >
                                        {api.connected ? 'Reconfigure' : t.connect}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === 'destinations' && (
                    <div className={`p-6 rounded-2xl ${bgCard}`}>
                        <h3 className="font-bold text-lg mb-4">{t.topDestinations}</h3>
                        <div className="h-80">
                            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
                                <BarChart data={topDestinations} layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e2e8f0'} horizontal={false} />
                                    <XAxis type="number" stroke={textSecondary} fontSize={12} />
                                    <YAxis dataKey="name" type="category" stroke={textSecondary} fontSize={12} width={80} />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: isDark ? '#1e293b' : '#fff',
                                            border: 'none',
                                            borderRadius: '12px'
                                        }}
                                    />
                                    <Bar dataKey="revenue" fill="#14b8a6" radius={[0, 8, 8, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}

                {activeTab === 'bookings' && (
                    <div className={`p-6 rounded-2xl ${bgCard}`}>
                        <h3 className="font-bold text-lg mb-4">{t.recentBookings}</h3>

                        {/* Bookings Table */}
                        <div className={`rounded-xl overflow-hidden ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                            <table className="w-full">
                                <thead className={isDark ? 'bg-slate-600/50' : 'bg-gray-100'}>
                                    <tr className="text-left text-xs uppercase tracking-wider">
                                        <th className="px-4 py-3 font-semibold">Booking ID</th>
                                        <th className="px-4 py-3 font-semibold">Customer</th>
                                        <th className="px-4 py-3 font-semibold">Destination</th>
                                        <th className="px-4 py-3 font-semibold">Check-in</th>
                                        <th className="px-4 py-3 font-semibold">Status</th>
                                        <th className="px-4 py-3 font-semibold text-right">Amount</th>
                                    </tr>
                                </thead>
                                <tbody className={`divide-y ${isDark ? 'divide-slate-600' : 'divide-gray-200'}`}>
                                    {recentBookings.map((booking: any, i: number) => (
                                        <tr key={i} className={`hover:${isDark ? 'bg-slate-600/50' : 'bg-gray-100'} transition-colors cursor-pointer`}>
                                            <td className="px-4 py-3 font-mono text-sm text-teal-500">{booking.id}</td>
                                            <td className="px-4 py-3 font-medium">{booking.customer}</td>
                                            <td className="px-4 py-3 text-sm">{booking.destination}</td>
                                            <td className={`px-4 py-3 text-sm ${textSecondary}`}>{booking.checkin}</td>
                                            <td className="px-4 py-3">
                                                <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${booking.status === 'confirmed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                                    booking.status === 'pending' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                                        'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                                    }`}>
                                                    {booking.status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-right font-bold">€{booking.amount.toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-600/30">
                            <span className={`text-sm ${textSecondary}`}>Showing {recentBookings.length} recent bookings</span>
                            <div className="flex gap-2">
                                <button className={`px-3 py-1 rounded-lg text-sm ${isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-100 hover:bg-gray-200'}`}>Previous</button>
                                <button className="px-3 py-1 rounded-lg text-sm bg-teal-500 text-white">1</button>
                                <button className={`px-3 py-1 rounded-lg text-sm ${isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-100 hover:bg-gray-200'}`}>2</button>
                                <button className={`px-3 py-1 rounded-lg text-sm ${isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-100 hover:bg-gray-200'}`}>3</button>
                                <button className={`px-3 py-1 rounded-lg text-sm ${isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-100 hover:bg-gray-200'}`}>Next</button>
                            </div>
                        </div>

                        {/* Summary Stats */}
                        <div className="grid grid-cols-3 gap-4 mt-6">
                            <div className={`p-4 rounded-xl ${isDark ? 'bg-slate-700/50' : 'bg-white'}`}>
                                <p className={`text-xs font-bold ${textSecondary}`}>CONFIRMED</p>
                                <p className="text-2xl font-black text-green-500">{bookingStats.confirmed}</p>
                            </div>
                            <div className={`p-4 rounded-xl ${isDark ? 'bg-slate-700/50' : 'bg-white'}`}>
                                <p className={`text-xs font-bold ${textSecondary}`}>PENDING</p>
                                <p className="text-2xl font-black text-yellow-500">{bookingStats.pending}</p>
                            </div>
                            <div className={`p-4 rounded-xl ${isDark ? 'bg-slate-700/50' : 'bg-white'}`}>
                                <p className={`text-xs font-bold ${textSecondary}`}>CANCELLED</p>
                                <p className="text-2xl font-black text-red-500">{bookingStats.cancelled}</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* API Key Modal */}
            {showApiKeyModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowApiKeyModal(null)}>
                    <div onClick={(e) => e.stopPropagation()} className={`w-full max-w-md rounded-3xl p-6 ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
                        <h2 className="text-xl font-bold mb-4">Configure API</h2>
                        <div className="mb-4">
                            <label className={`text-sm font-medium ${textSecondary} mb-2 block`}>API Key / Token</label>
                            <input
                                type="text"
                                value={apiKeyInput}
                                onChange={(e) => setApiKeyInput(e.target.value)}
                                placeholder="Enter your API key..."
                                className={`w-full p-3 rounded-xl border font-mono text-sm ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-200'}`}
                            />
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowApiKeyModal(null)}
                                className={`flex-1 py-3 rounded-xl font-medium ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => saveApiKey(showApiKeyModal)}
                                className="flex-1 py-3 rounded-xl font-medium bg-teal-500 text-white hover:bg-teal-600"
                            >
                                {t.saveSettings}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LumiReportingContainer;
